import { useEffect, useState } from 'react';

export type Breakpoint = 'mobile' | 'pad' | 'desktop';

export const BREAKPOINT_PAD_MIN = 768;
export const BREAKPOINT_DESKTOP_MIN = 1200;

function resolve(width: number): Breakpoint {
  if (width < BREAKPOINT_PAD_MIN) return 'mobile';
  if (width < BREAKPOINT_DESKTOP_MIN) return 'pad';
  return 'desktop';
}

/**
 * Single source of truth for responsive branching across admin / operator.
 * mobile < 768px <= pad < 1200px <= desktop. Listens to matchMedia so it
 * only re-renders when crossing a breakpoint boundary, not on every resize.
 */
export function useBreakpoint(): Breakpoint {
  const [bp, setBp] = useState<Breakpoint>(() =>
    typeof window === 'undefined' ? 'desktop' : resolve(window.innerWidth),
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const padQuery = window.matchMedia(`(min-width: ${BREAKPOINT_PAD_MIN}px)`);
    const desktopQuery = window.matchMedia(`(min-width: ${BREAKPOINT_DESKTOP_MIN}px)`);
    const update = () => setBp(resolve(window.innerWidth));
    update();
    padQuery.addEventListener('change', update);
    desktopQuery.addEventListener('change', update);
    return () => {
      padQuery.removeEventListener('change', update);
      desktopQuery.removeEventListener('change', update);
    };
  }, []);

  return bp;
}
